import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";

//maerial ui compoents
import {
  Box,
  Card,
  Divider,
  Grid,
  Link,
  Stack,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
  LinearProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useTheme } from "@mui/material/styles";

import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import LockOpenIcon from "@mui/icons-material/LockOpen";

//project components
import defaultAvatar from "../../../../assets/images/default-avatar.webp";
import config from "../../../../config";
import { setUser } from "../../../../store/userSlice";
import ChangePassword from "../../../common/auth/ChangePassword";

//third party
import axios from "axios";
import useAxiosAuth from "../../../../hooks/useAxiosAuth";
import { BASE_URL } from "../../../../api";

const SidePanel = (props) => {
  //Constants
  const { candidate, onSave, setSelectedPage, documents } = props;
  const { userId, userName, role, displayRole } = useSelector(
    (state) => state.userReducer
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const axiosAuth = useAxiosAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  //States
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [resumeFile, setResumeFile] = useState(undefined);
  const [aadharFile, setAadharFile] = useState(undefined);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [openChangePassword, setOpenChangePassword] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [openRestoreConfirmation, setOpenRestoreConfirmation] = useState(false);
  const [photo, setPhoto] = useState(undefined);
  const [photoURL, setPhotoURL] = useState(undefined);
  const [photoPreview, setPhotoPreview] = useState(undefined);
  const [photoVersion, setPhotoVersion] = useState(Date.now());
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };
  function LinearProgressWithLabel(props) {
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box sx={{ width: "100%", mr: 1 }}>
          <LinearProgress variant="determinate" {...props} />
        </Box>
        <Box sx={{ minWidth: 35 }}>
          <Typography variant="body2" color="text.secondary">{`${Math.round(
            props.value
          )}%`}</Typography>
        </Box>
      </Box>
    );
  }
  //Methods
  const handleListItemClick = (event, index) => {
    if (index <= 1) {
      setSelectedIndex(index);
      setSelectedPage(index);
    } else if (index === 2) {
      setOpenChangePassword(true);
    } else if (index === 3) {
      setOpenDeleteConfirmation(true);
    } else if (index === 4) {
      setOpenRestoreConfirmation(true);
    }
  };

  const handleLogout = async () => {
    localStorage.removeItem("accessToken");
    dispatch(
      setUser({
        userId: 0,
        profileId: 0,
        userName: "",
        role: 0,
        displayRole: "",
        accessToken: "",
      })
    );
    navigate("/");
  };
  const handleDeleteAccount = () => {
    axiosAuth
      .delete("/api/Candidates/soft-delete?id=" + candidate?.id)
      .then((response) => {
        if (response.status === 200) {
          showSnackbar("Account deleted successfully");
          navigate("/admin/candidates");
        }
      })
      .catch((error) => {
        if (error.response && error?.response?.status === 404) {
          showSnackbar(
            "Candidate is already registered and cannot be deleted",
            "error"
          );
        } else if (error.response && error.response?.status === 400) {
          showSnackbar("Candidate not found", "error");
        } else {
          showSnackbar("Failed to delete account", "error");
        }
      })
      .finally(() => {
        setOpenDeleteConfirmation(false);
      });
  };

  // Update your restore handler similarly
  const handleRestoreAccount = () => {
    axiosAuth
      .delete("/api/Candidates/restore?id=" + candidate.id)
      .then((response) => {
        if (response.status === 200) {
          showSnackbar("Account restored successfully");
          navigate(-1);
        }
      })
      .catch((error) => {
        showSnackbar("Failed to restore account", "error");
      })
      .finally(() => {
        setOpenRestoreConfirmation(false);
      });
  };
  //UseEffects
  useEffect(() => {
    if (candidate.photo != null) {
      // Add a timestamp query parameter to prevent browser caching
      setPhotoURL(
        `${BASE_URL}/UplodedFiles/${candidate.photo}?v=${photoVersion}`
      );
    } else {
      setPhotoURL(undefined);
    }
  }, [candidate, photoVersion]);

  useEffect(() => {
    if (photo) {
      const previewURL = URL.createObjectURL(photo);
      setPhotoPreview(previewURL);
      return () => URL.revokeObjectURL(previewURL);
    }
  }, [photo]);

  useEffect(() => {
    if (photo === undefined) {
      return;
    }

    setUploading(true); // Add this if you have a loading state

    const formData = new FormData();
    formData.append("file", photo, candidate.id + "-profile.jpg");

    axiosAuth
      .post("/api/Candidates/uploadPhoto/" + candidate.id, formData)
      .then((response) => {
        if (response.status === 200) {
          // Update the photo version to force refresh
          setPhotoVersion(Date.now());

          // Clean up states
          setPhoto(undefined);
          setPhotoPreview(undefined);

          onSave();
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setUploading(false);
      });
  }, [photo]);

  const getDocumentPath = (documents, type) => {
    if (!documents || !Array.isArray(documents)) return null;

    // type: 1 for resume, 2 for aadhar
    const document = documents?.find(
      (doc) =>
        (type === 1 && doc.resumePath) || (type === 2 && doc.aadharCardPath)
    );

    return document
      ? type === 1
        ? document.resumePath
        : document.aadharCardPath
      : null;
  };
  const resumePath = getDocumentPath(documents, 1);
  const aadharPath = getDocumentPath(documents, 2);
  console.log("documents", resumePath, aadharFile, candidate);

  return (
    <Card elevation={3} sx={{ padding: 2, borderRadius: "6px" }}>
      <Stack alignItems="center" spacing={2}>
        <Stack alignItems="center">
          {photoPreview ? (
            <img src={photoPreview} alt="Preview" width="150px" />
          ) : candidate.photo != null && photoURL ? (
            <img
              src={photoURL}
              alt="User"
              width="150px"
              key={photoVersion} // Add a key to force re-render when URL changes
            />
          ) : (
            <img src={defaultAvatar} alt="User" width="150px" />
          )}

          <label htmlFor="btn-upload-photo">
            <input
              id="btn-upload-photo"
              name="btn-upload-1"
              style={{ display: "none" }}
              type="file"
              multiple={false}
              accept="application/jpg"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setPhoto(e.target.files[0]);
                }
              }}
            />
            <Button component="span" size="small">
              Change Photo
            </Button>
          </label>
        </Stack>
        <Box>
          <Typography
            variant="h5"
            color="primary"
            sx={{ textAlign: "center", fontWeight: "bold" }}
          >
            {candidate.salutation}
            {candidate.firstName} {candidate.middleName} {candidate.lastName}
          </Typography>
          <Typography variant="h6" sx={{ textAlign: "center", color: "gray" }}>
            {candidate.city}
          </Typography>
        </Box>
        {/* <Box sx={{ width: "100%" }}>
          <LinearProgressWithLabel
            value={candidate?.profileCompletionValue}
            color="secondary"
          />
          <Typography variant="subtitle2" textAlign="center">
            Profile
          </Typography>
        </Box> */}
        <Divider sx={{ marginY: 2 }} />
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5}>
            <PhoneIcon fontSize="small" sx={{ color: "#2A569F" }} />
            <Typography variant="body1">{candidate.phoneNumber}</Typography>
          </Stack>
          <Stack direction="row" spacing={1.5}>
            <EmailIcon fontSize="small" sx={{ color: "#2A569F" }} />
            <Typography variant="body1">{candidate.email}</Typography>
          </Stack>
        </Stack>
      </Stack>
      <Divider sx={{ marginY: 2 }} />
      <Stack spacing={2}>
        <Typography variant="h5" textAlign="center">
          Resume
        </Typography>
        <label htmlFor="btn-upload-1">
          <input
            id="btn-upload-1"
            name="btn-upload-1"
            style={{ display: "none" }}
            type="file"
            multiple={false}
            accept="application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => {
              setResumeFile(e.target.files[0]);
            }}
          />
          <Button variant="outlined" component="span" fullWidth>
            {resumePath ? "Change File" : "Choose File"}
          </Button>
        </label>
        {resumeFile != undefined && (
          <LoadingButton
            fullWidth
            disabled={resumeFile === undefined}
            color="primary"
            loading={uploading}
            loadingPosition="end"
            endIcon={<FileUploadRoundedIcon />}
            variant="contained"
            onClick={() => {
              setUploading(true);
              const formData = new FormData();
              let fileExtension = "";
              if (resumeFile.type === "application/pdf") {
                fileExtension = ".pdf";
              } else if (
                resumeFile.type ===
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              ) {
                fileExtension = ".docx";
              } else {
                setUploading(false);
                setResumeFile(undefined);
                alert("Only PDF and WORD files are allowed");
                return;
              }
              formData.append(
                "file",
                resumeFile,
                candidate.id + "-resume" + fileExtension
              );
              const existingResumeDoc = documents?.find(
                (doc) => doc.resumePath
              );
              const documentId = existingResumeDoc ? existingResumeDoc.id : 0;
              axiosAuth
                .post(
                  "/api/CandidateDocuments/uploadFile/" +
                    documentId +
                    "?candidateId=" +
                    candidate.id +
                    "&type=" +
                    1,
                  formData
                )
                .then((response) => {
                  if (response.status === 200) {
                    setUploading(false);
                    setResumeFile(undefined);
                    onSave();
                  }
                })
                .catch((error) => {
                  console.log(error);
                  setUploading(false);
                });
            }}
          >
            Upload Resume
          </LoadingButton>
        )}
        {resumePath && (
          <LoadingButton
            fullWidth
            disabled={resumeFile != undefined}
            color="primary"
            loading={downloading}
            loadingPosition="end"
            endIcon={<FileDownloadRoundedIcon />}
            variant="contained"
            onClick={() => {
              setDownloading(true);
              axiosAuth
                .get(
                  `/api/CandidateDocuments/DownloadFile?fileName=${resumePath}`,
                  {
                    responseType: "blob", // Important for file downloads
                  }
                )
                .then((response) => {
                  // Create a blob URL for the file
                  const url = window.URL.createObjectURL(
                    new Blob([response.data])
                  );

                  // Create a temporary anchor element
                  const link = document.createElement("a");
                  link.href = url;

                  // Set the download filename
                  link.setAttribute(
                    "download",
                    `${candidate.firstName}_${candidate.lastName}_resume.pdf`
                  );

                  // Append to DOM and trigger click
                  document.body.appendChild(link);
                  link.click();

                  // Clean up
                  link.parentNode.removeChild(link);
                  window.URL.revokeObjectURL(url);
                  setDownloading(false);
                })
                .catch((error) => {
                  console.error("Download failed:", error);
                  setDownloading(false);
                  alert("Failed to download resume");
                });
            }}
          >
            Download Resume
          </LoadingButton>
        )}
        <Divider sx={{ marginY: 2 }} />
        <Typography variant="h5" textAlign="center">
          Adhar Card
        </Typography>
        <label htmlFor="btn-upload-2">
          <input
            id="btn-upload-2"
            name="btn-upload-2"
            style={{ display: "none" }}
            type="file"
            multiple={false}
            accept="application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => {
              setAadharFile(e.target.files[0]);
            }}
          />
          <Button variant="outlined" component="span" fullWidth>
            {aadharPath ? "Change File" : "Choose File"}
          </Button>
        </label>
        {aadharFile != undefined && (
          <LoadingButton
            fullWidth
            disabled={aadharFile === undefined}
            color="primary"
            loading={uploading}
            loadingPosition="end"
            endIcon={<FileUploadRoundedIcon />}
            variant="contained"
            onClick={() => {
              setUploading(true);
              const formData = new FormData();
              let fileExtension = "";
              if (aadharFile.type === "application/pdf") {
                fileExtension = ".pdf";
              } else if (
                aadharFile.type ===
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              ) {
                fileExtension = ".docx";
              } else {
                setUploading(false);
                setAadharFile(undefined);
                alert("Only PDF and WORD files are allowed");
                return;
              }
              formData.append(
                "file",
                aadharFile,
                candidate.id + "-aadhar" + fileExtension
              );
              const existingAadharDoc = documents?.find(
                (doc) => doc.aadharCardPath
              );
              const documentId = existingAadharDoc ? existingAadharDoc.id : 0;
              axiosAuth
                .post(
                  "/api/CandidateDocuments/uploadFile/" +
                    documentId +
                    "?candidateId=" +
                    candidate.id +
                    "&type=" +
                    2,
                  formData
                )
                .then((response) => {
                  if (response.status === 200) {
                    setUploading(false);
                    setAadharFile(undefined);
                    onSave();
                  }
                })
                .catch((error) => {
                  console.log(error);
                  setUploading(false);
                });
            }}
          >
            Upload Aadhar Card
          </LoadingButton>
        )}

        {aadharPath && (
          <LoadingButton
            fullWidth
            disabled={aadharFile != undefined}
            color="primary"
            loading={downloading}
            loadingPosition="end"
            endIcon={<FileDownloadRoundedIcon />}
            variant="contained"
            onClick={() => {
              setDownloading(true);

              // Find the document that has an aadharCardPath (type 2 document)
              const aadharDocument = documents?.find(
                (doc) => doc.aadharCardPath !== null
              );

              if (!aadharDocument) {
                setDownloading(false);
                alert("No Aadhar document found");
                return;
              }

              axiosAuth
                .get(
                  `/api/CandidateDocuments/adhar-card-download?id=${aadharDocument.id}`,
                  {
                    responseType: "blob",
                  }
                )
                .then((response) => {
                  // Create a blob URL for the file
                  const url = window.URL.createObjectURL(
                    new Blob([response.data])
                  );

                  // Create a temporary anchor element
                  const link = document.createElement("a");
                  link.href = url;

                  // Set the download filename
                  link.setAttribute(
                    "download",
                    `${candidate.firstName}_${candidate.lastName}_aadhar.pdf`
                  );

                  // Append to DOM and trigger click
                  document.body.appendChild(link);
                  link.click();

                  // Clean up
                  link.parentNode.removeChild(link);
                  window.URL.revokeObjectURL(url);
                  setDownloading(false);
                })
                .catch((error) => {
                  console.error("Download failed:", error);
                  setDownloading(false);
                  alert("Failed to download Aadhar card");
                });
            }}
          >
            Download Aadhar
          </LoadingButton>
        )}
      </Stack>
      <Divider sx={{ marginY: 2 }} />
      <Stack>
        <List>
          <ListItemButton
            sx={{
              padding: 1,
              borderLeft: "solid",
              borderLeftWidth: 4,
              borderLeftColor:
                selectedIndex === 0
                  ? theme.palette.primary.main
                  : theme.palette.white.main,
            }}
            selected={selectedIndex === 0}
            onClick={(event) => handleListItemClick(event, 0)}
          >
            <ListItemIcon>
              <AccountCircleOutlinedIcon sx={{ color: "#2A569F" }} />
            </ListItemIcon>
            <ListItemText primary="Profile" />
          </ListItemButton>

          <ListItemButton
            sx={{
              padding: 1,
              borderLeft: "solid",
              borderLeftWidth: 4,
              borderLeftColor:
                selectedIndex === 1
                  ? theme.palette.primary.main
                  : theme.palette.white.main,
            }}
            selected={selectedIndex === 1}
            onClick={(event) => handleListItemClick(event, 1)}
          >
            <ListItemIcon>
              <CheckBoxOutlinedIcon sx={{ color: "#2A569F" }} />
            </ListItemIcon>
            <ListItemText primary="Job Applications" />
          </ListItemButton>

          <Divider sx={{ my: 2 }} />
          {role !== 1 && (
            <ListItemButton
              sx={{
                padding: 1,
              }}
              onClick={(event) => handleListItemClick(event, 2)}
            >
              <ListItemIcon>
                <LockOpenIcon sx={{ color: "#2A569F" }} />
              </ListItemIcon>
              <ListItemText primary="Change Password" />
            </ListItemButton>
          )}
          {role === 1 && (
            <>
              {candidate.isDeleted == true ? (
                <ListItemButton
                  sx={{
                    padding: 1,
                  }}
                  onClick={(event) => handleListItemClick(event, 4)}
                >
                  <ListItemIcon sx={{ color: "#2A569F" }}>
                    <SettingsBackupRestoreIcon />
                  </ListItemIcon>
                  <ListItemText primary="Restore Account" />
                </ListItemButton>
              ) : (
                <ListItemButton
                  sx={{
                    padding: 1,
                  }}
                  onClick={(event) => handleListItemClick(event, 3)}
                >
                  <ListItemIcon sx={{ color: "#2A569F" }}>
                    <DeleteOutlineOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText primary="Delete Account" />
                </ListItemButton>
              )}
            </>
          )}

          <Divider sx={{ mt: 2 }} />
        </List>
      </Stack>

      {/* Delete dialog */}
      <Dialog
        open={openDeleteConfirmation}
        keepMounted
        onClose={() => {
          setOpenDeleteConfirmation(false);
        }}
      >
        <DialogTitle>{"Delete Account"}</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this account?</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleDeleteAccount}>
            Yes
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setOpenDeleteConfirmation(false);
            }}
          >
            No
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore dialog */}
      <Dialog
        open={openRestoreConfirmation}
        keepMounted
        onClose={() => {
          setOpenRestoreConfirmation(false);
        }}
      >
        <DialogTitle>{"Restore Account"}</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to restore this account?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleRestoreAccount}>
            Yes
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setOpenRestoreConfirmation(false);
            }}
          >
            No
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password dialog */}
      <Dialog
        fullWidth
        maxWidth="md"
        open={openChangePassword}
        onClose={() => {
          setOpenChangePassword(false);
        }}
      >
        <DialogContent>
          <ChangePassword
            userId={userId}
            onCancel={() => {
              setOpenChangePassword(false);
            }}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{
            width: "100%",
            bgcolor:
              snackbarMessage.severity === "success"
                ? "success.main"
                : "error.main",
            color: "common.white",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default SidePanel;
